import polyline from '@mapbox/polyline';

export type LngLat = { lng: number; lat: number };
export type Segment = { coords: [number, number][]; meters: number };
export type ActivityProfile = 'pedestrian' | 'bicycle';

export function haversineMeters(a: [number, number], b: [number, number]): number {
  const R = 6371008.8; // Earth mean radius (IUGG)
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export class Route {
  waypoints: LngLat[] = [];
  segments: Segment[] = [];

  get meters(): number {
    return this.segments.reduce((s, seg) => s + seg.meters, 0);
  }

  addWaypoint(wp: LngLat, snap: boolean): void {
    const prev = this.waypoints.at(-1);
    this.waypoints.push(wp);
    if (!prev) return;
    if (snap) {
      throw new Error('snap routing not implemented yet (Task 3.6)');
    }
    const a: [number, number] = [prev.lng, prev.lat];
    const b: [number, number] = [wp.lng, wp.lat];
    this.segments.push({ coords: [a, b], meters: haversineMeters(a, b) });
  }

  /**
   * Atomic: the waypoint and (if applicable) its segment are both pushed
   * only after fetching succeeds. If Valhalla returns an error or the
   * network drops, this method throws and the route's state is unchanged
   * — no orphaned waypoint, no off-by-one between waypoints and segments.
   */
  async addWaypointAsync(wp: LngLat, snap: boolean, profile: ActivityProfile): Promise<void> {
    const prev = this.waypoints.at(-1);

    // First waypoint - no segment to compute.
    if (!prev) {
      this.waypoints.push(wp);
      return;
    }

    if (!snap) {
      const a: [number, number] = [prev.lng, prev.lat];
      const b: [number, number] = [wp.lng, wp.lat];
      const seg: Segment = { coords: [a, b], meters: haversineMeters(a, b) };
      this.waypoints.push(wp);
      this.segments.push(seg);
      return;
    }

    // Snap on: fetch BEFORE mutating, so a failure leaves the route untouched.
    const seg = await fetchSegment(prev, wp, profile);
    this.waypoints.push(wp);
    this.segments.push(seg);
  }

  /**
   * Transactional: builds the new segment list in a temporary buffer and
   * swaps it in only after every leg has fetched successfully. If any
   * leg fails, the existing waypoints + segments are preserved verbatim.
   */
  async recomputeAll(snap: boolean, profile: ActivityProfile): Promise<void> {
    const wps = [...this.waypoints];
    if (wps.length < 2) {
      // Nothing to compute - either empty or a single floating waypoint.
      this.segments = [];
      return;
    }

    const newSegments: Segment[] = [];
    for (let i = 1; i < wps.length; i++) {
      const prev = wps[i - 1]!;
      const next = wps[i]!;
      if (snap) {
        // Throws on failure - the running tally and `this.segments`
        // both stay untouched, leaving the previous good route in place.
        newSegments.push(await fetchSegment(prev, next, profile));
      } else {
        const a: [number, number] = [prev.lng, prev.lat];
        const b: [number, number] = [next.lng, next.lat];
        newSegments.push({ coords: [a, b], meters: haversineMeters(a, b) });
      }
    }
    // All legs succeeded - swap atomically.
    this.segments = newSegments;
  }

  /**
   * Replace the route with a prebaked waypoint list + per-pair segments.
   * Used when loading a saved route from /r/:id - bypasses Valhalla
   * because the geometry was already computed at save time.
   *
   * The caller passes per-pair segments so that subsequent edits (undo,
   * adding new waypoints) keep the segment-per-pair invariant. If you
   * only have one big polyline, call recomputeAll() instead.
   */
  loadPrebaked(wps: LngLat[], segments: Segment[]): void {
    this.waypoints = [...wps];
    this.segments = [...segments];
  }

  undo(): void {
    this.waypoints.pop();
    this.segments.pop();
  }

  clear(): void {
    this.waypoints = [];
    this.segments = [];
  }

  geometry(): [number, number][] {
    return this.segments.flatMap((s) => s.coords);
  }
}

async function fetchSegment(
  from: LngLat,
  to: LngLat,
  profile: ActivityProfile,
): Promise<Segment> {
  const res = await fetch('/api/route', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      locations: [
        { lat: from.lat, lon: from.lng },
        { lat: to.lat, lon: to.lng },
      ],
      costing: profile,
      directions_options: { units: 'kilometers' },
    }),
  });
  if (!res.ok) throw new Error(`route ${res.status}`);
  const data = await res.json();
  const shape = data?.trip?.legs?.[0]?.shape;
  const km = data?.trip?.summary?.length;
  if (typeof shape !== 'string' || typeof km !== 'number') {
    throw new Error('malformed Valhalla response');
  }
  // Valhalla polyline6: [lat, lng]; we store [lng, lat].
  const decoded = polyline.decode(shape, 6) as [number, number][];
  const coords: [number, number][] = decoded.map(([lat, lng]) => [lng, lat]);
  return { coords, meters: km * 1000 };
}
